/**
 * DevDocShareCard — prominent CTA inside the Builder's Tracking step
 * ──────────────────────────────────────────────────────────────────
 *
 * The operator's primary action: get a single shareable link to send
 * the destination site's dev team. Replaces the previous "copy 4
 * different snippets and explain them yourself" workflow.
 *
 * Build flow:
 *   1. Fetch /api/capi/affiliate-config/:pageId to get the page's
 *      postbackSecret (auto-generates on first call). Why fetch
 *      from this existing endpoint instead of building the URL
 *      client-side? The secret is server-owned — we never want it
 *      hardcoded or guessable from the page ID alone.
 *   2. Compose the share URL: `<origin>/dev/<pageId>?token=<secret>`.
 *      We use window.location.origin so the link works on whatever
 *      domain the operator is currently on (custom domains, dev,
 *      staging, production).
 *   3. Render a copy button + "open preview" button. Mobile-friendly,
 *      single-tap workflow.
 *
 * Failure modes the UI handles:
 *   - Page not yet saved (config endpoint returns 404 NOT_SAVED) →
 *     "Save the page first" hint instead of a broken URL.
 *   - Postback explicitly disabled by the operator → "Enable postback
 *     to generate a share link" hint with a hint pointing to the
 *     toggle in the advanced section below.
 *   - Auth expired → fall through to the existing AuthExpired hint
 *     pattern used elsewhere in the Tracking step.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Copy,
  CheckCircle2,
  ExternalLink,
  AlertCircle,
  Link2,
  BookOpen,
  Loader2,
  RefreshCw,
} from 'lucide-react';

// IMPORTANT: rest of codebase configures VITE_API_URL with the `/api`
// suffix already baked in (e.g. `https://api.hitzlink.com/api`) and
// concatenates routes as `${API_URL}/pages`, `${API_URL}/capi/...`,
// etc. We MUST follow the same convention here — concatenating
// `${API_URL}/api/capi/...` in production produced
// `https://api.hitzlink.com/api/api/capi/...` and 404'd. The fallback
// also needs the `/api` suffix so local dev works without override.
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface AffiliateConfigShape {
  enabled: boolean;
  postbackSecret: string | null;
}

interface Props {
  pageId: string;
  /**
   * Bumped by the parent (Builder) every time the page is saved.
   * The card watches this value and re-fetches its config — without
   * it the operator would click "Save" on a fresh page and the share
   * link wouldn't appear until the next tab focus, leaving the
   * "Save the page first" hint in place and looking broken.
   */
  savedAt?: number;
}

export function DevDocShareCard({ pageId, savedAt }: Props) {
  const { t } = useTranslation('tracking');
  const [config, setConfig] = useState<AffiliateConfigShape | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'NOT_SAVED' | 'AUTH' | string | null>(null);
  const [lastStatus, setLastStatus] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  // Tracks whether we've already issued the one-shot self-heal POST/PUT
  // for this page. Stops us from looping if the server is genuinely
  // unhealthy (e.g. DB down) — we'd rather show a clear error than
  // hammer the API with a retry storm.
  const selfHealAttemptedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pageId) return;
    selfHealAttemptedRef.current = null;
    void load();
    // Refresh when the postback enable toggle in the advanced
    // section flips — no event bus, so we re-poll on focus instead.
    // Cheap (one cached request) and converges within ~1 visibility
    // change. If we ever add a real bus we can drop this.
    const onVis = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
    // We intentionally re-run when `savedAt` changes — that's the
    // signal from Builder that the page has just been saved and the
    // affiliate-config row now exists server-side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, savedAt]);

  async function load(opts: { skipSelfHeal?: boolean } = {}) {
    try {
      setLoading(true);
      setError(null);
      const tok = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/capi/affiliate-config/${pageId}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      setLastStatus(res.status);

      if (res.status === 404) {
        // First-touch self-heal:
        //
        // The GET handler auto-generates the postbackSecret on first call,
        // BUT only after the page exists in `landing_pages`. Some flows
        // we've seen in the wild ended up with the row's `settings`
        // JSONB getting partially replaced by a stale Builder save that
        // dropped the affiliate sub-key, leaving the GET to spuriously
        // read `aff = {}` and 404 the user (the legacy 404 path was
        // hit when the *row* was missing — modern handlers also 404 on
        // empty settings until first PUT). One PUT with `enabled:true`
        // forces the server to re-materialise the row's affiliate
        // section, after which the GET succeeds for good.
        //
        // We fire this AT MOST ONCE per pageId per mount so a genuinely
        // missing page (e.g. user typed a wrong URL) still surfaces the
        // friendly "save first" hint instead of an infinite spinner.
        if (!opts.skipSelfHeal && selfHealAttemptedRef.current !== pageId) {
          selfHealAttemptedRef.current = pageId;
          try {
            const putRes = await fetch(
              `${API_URL}/capi/affiliate-config/${pageId}`,
              {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
                },
                body: JSON.stringify({ enabled: true }),
              }
            );
            if (putRes.ok) {
              // Server has now (re)created the affiliate sub-tree —
              // re-run the GET. `skipSelfHeal: true` so we don't
              // recurse if the GET still 404s for some other reason.
              return load({ skipSelfHeal: true });
            }
          } catch {
            // Network blip — fall through to NOT_SAVED hint below.
          }
        }
        setError('NOT_SAVED');
        setConfig(null);
        return;
      }
      if (res.status === 401 || res.status === 403) {
        setError('AUTH');
        setConfig(null);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setConfig({ enabled: data.enabled !== false, postbackSecret: data.postbackSecret });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Compose the share URL. Origin is taken from the current window
  // so the link is correct across custom domains / dev / staging.
  const shareUrl =
    config?.postbackSecret && config.enabled
      ? `${window.location.origin}/dev/${pageId}?token=${encodeURIComponent(
          config.postbackSecret
        )}`
      : null;

  function copyLink() {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // ─── Loading / error / disabled states ───────────────────────────

  if (loading) {
    return (
      <Card>
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Loader2 size={14} className="animate-spin" />
          {t('devDocCard.loading')}
        </div>
      </Card>
    );
  }

  if (error === 'NOT_SAVED') {
    return (
      <Card>
        <Header />
        <Hint variant="info" icon={<AlertCircle size={14} />}>
          {t('devDocCard.notSaved')}
        </Hint>
        <DiagnosticFooter
          pageId={pageId}
          status={lastStatus}
          onRetry={() => {
            selfHealAttemptedRef.current = null;
            void load();
          }}
        />
      </Card>
    );
  }

  if (error === 'AUTH') {
    return (
      <Card>
        <Header />
        <Hint variant="warn" icon={<AlertCircle size={14} />}>
          {t('devDocCard.authExpired')}
        </Hint>
      </Card>
    );
  }

  if (config && !config.enabled) {
    return (
      <Card>
        <Header />
        <Hint variant="info" icon={<AlertCircle size={14} />}>
          {t('devDocCard.postbackDisabled')}
        </Hint>
      </Card>
    );
  }

  if (!shareUrl) {
    return (
      <Card>
        <Header />
        <Hint variant="warn" icon={<AlertCircle size={14} />}>
          {t('devDocCard.unavailable')}
        </Hint>
        <DiagnosticFooter
          pageId={pageId}
          status={lastStatus}
          onRetry={() => {
            selfHealAttemptedRef.current = null;
            void load();
          }}
        />
      </Card>
    );
  }

  // ─── Happy path ───────────────────────────────────────────────────

  return (
    <Card>
      <Header />

      <div className="mt-3 space-y-2">
        <label className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {t('devDocCard.urlLabel')}
        </label>
        <div className="flex flex-wrap items-stretch gap-2">
          <input
            type="text"
            value={shareUrl}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            className="min-w-0 flex-1 rounded-md border border-emerald-500/30 bg-gray-950 px-3 py-2 font-mono text-xs text-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
          />
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-xs font-medium text-white transition hover:bg-emerald-700"
          >
            {copied ? (
              <>
                <CheckCircle2 size={14} />
                {t('devDocCard.copied')}
              </>
            ) : (
              <>
                <Copy size={14} />
                {t('devDocCard.copy')}
              </>
            )}
          </button>
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-700 bg-gray-800/40 px-3 py-2 text-xs font-medium text-gray-200 transition hover:bg-gray-800"
          >
            <ExternalLink size={14} />
            {t('devDocCard.preview')}
          </a>
        </div>
      </div>

      <ul className="mt-4 space-y-1.5 text-[11px] text-gray-400">
        <li className="flex items-start gap-1.5">
          <CheckCircle2 size={12} className="mt-0.5 flex-shrink-0 text-emerald-400" />
          <span>{t('devDocCard.feature1')}</span>
        </li>
        <li className="flex items-start gap-1.5">
          <CheckCircle2 size={12} className="mt-0.5 flex-shrink-0 text-emerald-400" />
          <span>{t('devDocCard.feature2')}</span>
        </li>
        <li className="flex items-start gap-1.5">
          <CheckCircle2 size={12} className="mt-0.5 flex-shrink-0 text-emerald-400" />
          <span>{t('devDocCard.feature3')}</span>
        </li>
      </ul>

      <p className="mt-3 text-[10px] text-gray-500 leading-relaxed">
        {t('devDocCard.security')}
      </p>
    </Card>
  );
}

// ─── Sub-components ───────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.07] to-blue-500/[0.04] p-4">
      {children}
    </div>
  );
}

function Header() {
  const { t } = useTranslation('tracking');
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-500/20 text-emerald-300">
        <BookOpen size={18} />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          {t('devDocCard.title')}
          <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-300">
            <span className="inline-flex items-center gap-1">
              <Link2 size={10} />
              {t('devDocCard.badge')}
            </span>
          </span>
        </h3>
        <p className="mt-0.5 text-xs text-gray-400 leading-relaxed">
          {t('devDocCard.description')}
        </p>
      </div>
    </div>
  );
}

function Hint({
  variant,
  icon,
  children,
}: {
  variant: 'info' | 'warn';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const cls =
    variant === 'info'
      ? 'border-blue-500/30 bg-blue-500/5 text-blue-200'
      : 'border-amber-500/30 bg-amber-500/5 text-amber-200';
  return (
    <div className={`mt-3 flex items-start gap-2 rounded-md border p-2.5 text-xs ${cls}`}>
      <span className="mt-0.5 flex-shrink-0">{icon}</span>
      <span className="leading-relaxed">{children}</span>
    </div>
  );
}

/**
 * Always-on retry + diagnostic strip for the error states.
 *
 * The big improvement over a blind "save again" hint: the operator
 * can fix the most common transient cause (request raced the save)
 * without leaving the page, and support can ask them to read off the
 * page id + last HTTP status if a real bug needs investigation.
 *
 * On a persistent 404 we additionally offer a "Reset" path. The most
 * common cause of a hard 404 is a stale persisted `currentPage` in
 * localStorage (zustand `persist`) — typically from another account
 * or from a deleted page. Re-routing the operator through the
 * dashboard re-loads pages from the server and picks a real id.
 */
function DiagnosticFooter({
  pageId,
  status,
  onRetry,
}: {
  pageId: string;
  status: number | null;
  onRetry: () => void;
}) {
  const { t } = useTranslation('tracking');
  const showReset = status === 404;
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-700/40 pt-2 text-[10px] text-gray-500">
      <span className="font-mono leading-tight">
        {`page: ${pageId.slice(0, 8)}…`}
        {status !== null ? ` · last: HTTP ${status}` : ''}
      </span>
      <div className="flex items-center gap-1.5">
        {showReset && (
          <button
            type="button"
            onClick={() => {
              try {
                // Drop the persisted currentPage so the dashboard
                // forces a clean re-pick from the server's truth.
                localStorage.removeItem('builder-storage');
              } catch {
                // localStorage can throw in private browsing — the
                // dashboard reload below is still useful.
              }
              window.location.assign('/dashboard');
            }}
            className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-200 transition hover:bg-amber-500/20"
            title={t('devDocCard.resetHint')}
          >
            {t('devDocCard.reset')}
          </button>
        )}
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1 rounded-md border border-gray-700 bg-gray-800/40 px-2 py-1 text-[11px] font-medium text-gray-200 transition hover:bg-gray-800"
        >
          <RefreshCw size={11} />
          {t('devDocCard.retry')}
        </button>
      </div>
    </div>
  );
}
